import {lockr, React, useContext, _, stringify, computedFn} from '@api'
import {ImageWrapperDefinition} from '@api/graphql'
import {Keys} from '@blueprintjs/core'
import {AbstractSidebarStore, ISidebarContext, SidebarModalStore, utils, IIndividualSidebarState, IModalDetailSearchResult, CatalogItemDetailsStore, cloneDeepNoPrivateFields, SidebarTableRowStore, dataMapperConfig, DEBUG_CATALOG_ITEM_CARDS, ISidebarWhereClause, useStores, SidebarAlign, ICatalogItemDetailsPathElement} from '@modules'
import {DetailsSecondariesStore} from '@design-board/stores'
import {CatalogItemChangeHistoryStore} from '@modules/CatalogItemDetails'
import {configs, site, DragDropEventHandlers, OFFLINE_ID, ui, LibraryItemConfig, ModalTabConfig, DetailsSectionConfig, DetailsViewFieldConfig, placeholderImages, SecondaryData, DetailsViewSectionConfig, CatalogImageSourceConfig, StoresInstance, IDataMapConfig, IUpload, CatalogImageService, Workspace, nav, IFlexLayoutInlineCatalogItemDetailsConfig, CatalogStore, ICatalogFieldConfig, ImageRecord, IImageOperationInput} from '@stores'
import {VipLocalStore} from '@modules'
import {ElasticSearchError} from '@stores/elasticsearch'
import {differenceInObject, guid} from '@utils'
import {SidebarCarouselStore} from 'centric-ui-components'
import {action, autorun, computed, observable, reaction, runInAction, toJS, trace, when} from 'mobx'
import {removeUndefinedsNested} from '@utils'
import {IDisposer} from 'mobx-utils'

const DEBUG_SIDEBAR_DETAILS = false && DEBUG

export const CatalogItemSidebarDetailsContext = React.createContext<CatalogItemSidebarDetailsStore>(undefined)
export const useCatalogItemSidebarDetailsStore = () => useContext(CatalogItemSidebarDetailsContext)
// export const useSidebarDetailsStore = (align: 'right' | 'left') => {
//   const {sidebar} = useStores()
//   return sidebar[align]?.details
// }

export type ImageSourceField = string
export interface SidebarDetailsProps {
  className?: string
  library?: LibraryItemConfig
  catalogField?: string
  esIndex?: string
  where?: Array<ISidebarWhereClause>
  idWithPath?: string
  context?: ISidebarContext
  align: 'left' | 'right' | 'modal' | 'inline'
  isCreate?: boolean
  _data?: Object
  style?: React.CSSProperties
  debugId?: string

  navItem?: ICatalogItemDetailsPathElement
}

enum SPEC_TYPE {
  COLORWAY = 'colorway'
}

export interface ISidebarDetailsStore {
  adoptionStore?
  secondariesStore?
  isCreate?: boolean
  isFirstRender?: boolean
  mutationSaveCatalogItemDetails?: Function
  updateSectionData?: Function
  saveOfflineChanges?: Function
  data?: any
  invalidFields?: any
  index?
  isValidForUpload?: boolean
  detailDef?
  isDirty?: boolean
  originalData?: any
  sidebar?: AbstractSidebarStore & Partial<IIndividualSidebarState>
  updateFieldData?: Function
  searchResults?
  isSearching?: boolean
  searchTabIndex?: number
  storeMap?
  activeTabStore?
  modal?: SidebarModalStore
  id?: string
  isOffline?: boolean
  currentSearchResult?: IModalDetailSearchResult
  selectedDetailTab?: Partial<ModalTabConfig>
  createObjectInRemote?: Function
  anchorEl?: HTMLElement
  isSyncing?: boolean
  syncDetails?: (id, index) => void
  itemDetailsStore?: CatalogItemDetailsStore
  isPublished?: boolean

  localImages?: Array<ImageWrapperDefinition>
}

export class CatalogItemSidebarDetailsStore extends VipLocalStore implements ISidebarDetailsStore {
  constructor(stores: StoresInstance, public props: SidebarDetailsProps) {
    super(stores)

    // console.trace(`Create CatalogItemSidebarDetailsStore`, {...props})
    this.modal = new SidebarModalStore(stores, this)
    if (props) {
      this.align = props.align
    }

    const {associatedEsIndex, workspaces} = this
    if (associatedEsIndex && !this.detailDef) {
      throw new Error(`No detail definition (sidebar or modal) is configured for the library '${associatedEsIndex}' (Workspace: '${workspaces.workspaceConfig?._id}')`)
    }
  }

  @observable.ref itemDetailsStore: CatalogItemDetailsStore

  @observable.ref changeHistoryStore: CatalogItemChangeHistoryStore

  @observable hasLoadedData = false

  /**
   * So each details store can guarantee it's own unique react instance
   */
  guid = guid()
  @observable.ref _carouselStore: SidebarCarouselStore

  get carouselStore() {
    return this?.modal?.carousel ?? this._carouselStore
  }

  @computed get doesOfflineItemExist() {
    const {isOffline, offlineId} = this
    return isOffline && _.has(CatalogStore._offlineItemsMap, offlineId)
  }

  @computed get selectedImage() {
    const {localImages, selectedImageIndex} = this
    return _.isEmpty(localImages) ? null : localImages[selectedImageIndex ?? 0]
  }

  @computed get dataMap(): IDataMapConfig {
    return this.associatedWorkspace?.dataMap ?? dataMapperConfig.specDataMapperConfig
  }

  @computed get isForNav() { return this.props?.context === 'navItem' && this.align === 'right'}

  @computed get associatedWorkspace(): Workspace {
    const {associatedEsIndex} = this
    return this.workspaces.wrapperByIndex[associatedEsIndex ?? this.index] ?? this.catalog.workspace
  }

  registerEffects = () => {
    const {isForNav, props, catalog, catalog: {vlp, sidebar}} = this
    if (!isForNav) {
      this.sidebar._details = this
    }

    autorun(() => {
      const {isCreate} = props, {index} = this

      // trace()
      runInAction(() => {
        if (this.itemDetailsStore) { this.itemDetailsStore.dispose()}
        this.itemDetailsStore = new CatalogItemDetailsStore(catalog, index, isCreate, this)
        this.itemDetailsStore.registerEffects()
      })
    }, {name: `Initialize item details store based on sidebar state`}),

// console.log(`Mount CatalogItemSidebarDetailsStore`, {isForNav, props})
      when(() => catalog.hasLoadedConfig, () => {
        this._toDispose = [
          !isForNav && autorun(() => {
            const {searchResults} = this
            runInAction(`Set search tab index when search results change`, () => {
              this.searchTabIndex = Math.max(0, searchResults.findIndex(f => f.tab == this.sidebar.selectedTab))
            })
          }, {name: `Set search tab index when search results change`}),

          !isForNav && autorun(() => {
            this.sidebar.tab && this.seenTabs.add(this.sidebar.tab.toString())
          }, {name: `Mark modal tab as seen when user opens the tab (load the contents)`}),

          !isForNav && autorun(() => {
            if (this.sidebar.open) {
              if (this.itemDetailsStore) {
                this.itemDetailsStore.wasChanged = false
              }
            } else {
              if (this.refetchOnClose) {
                this.refetchOnClose = false
                catalog.refetch()
              }
            }
          }, {name: `Refetch catalog item when sidebar is closed after changes have been made`}),

          !isForNav && autorun(() => {
            const {itemDetailsStore, sidebar: {open}, library} = this
            if (!open && itemDetailsStore?.wasChanged) {
              catalog.refetch(library ? library.name : 'catalogItems')
            }
          }, {name: `Refetch catalog items after closing details store when changes have been made`}),

          autorun(async () => {
            if (this.isOffline) {
              const {offlineData, catalog: {hasLoadedConfig}} = this,
                clone = cloneDeepNoPrivateFields(offlineData)

              runInAction(() => {
                offlineData && this.itemDetailsStore && this.itemDetailsStore.replaceData(clone, false)
                this.isInitialLoad = false
                this.hasLoadedData = true
                this.loading = false

                if (!this.data && hasLoadedConfig) {
                  // console.debug(`Removing invalid offline path`)
                  runInAction(() => {
                    const {sidebar} = this
                    sidebar.path.splice(sidebar.path.length - 1, 1)
                    sidebar.open = false
                  })
                }
              })
            } else {

              const {/*sidebar: {open}*/ index, id, itemDetailsStore, catalog: {lastModifiedItem}} = this

              if (/*!open && */lastModifiedItem?.index === index && lastModifiedItem?.id === id) {
                await runInAction(`Refetch sidebar details when item has been modified elsewhere (YFTS-6813)`, async () => {
                  if (itemDetailsStore?.data && lastModifiedItem?.data) {
                    itemDetailsStore.replaceData(lastModifiedItem.data)
                  } else {
                    await this.loadData()
                  }
                })
              }
            }
          }, {name: `Refetch sidebar details when item has been modified elsewhere (YFTS-6813, YFTS-8800)`}),

          !isForNav && reaction(() => vlp.hierarchy.selected, () => {
            this.where && this.align == 'left' && sidebar.left.onGoBack()
          }, {name: 'return to mpv list on hierarchy change from mpv details left sidebar'}),

          reaction(() => sidebar.right.open, (open) => {
            !open && this.itemDetailsStore && (this.itemDetailsStore.mutationData = {})
          }, {name: 'clearing mutation data on sidebar closed'}),

          reaction(() => this.localImages, () => {
            this.sidebar.showImageFullscreen && _.size(this.localImages) == 0 && this.onCloseSlider()
          }, {name: 'close Lightbox Image slider if Local images gets empty'}),

          autorun(() => {
            const {activeCatalog, carouselStore, id, isDirty} = this
            if (!isDirty && activeCatalog) {
              const {focusedCatalogItemStore} = activeCatalog
              if (carouselStore && focusedCatalogItemStore && id === focusedCatalogItemStore.id) {
                const {carouselStore: {selectedImage: carouselImage}} = this,
                  {selectedImage} = focusedCatalogItemStore

                selectedImage && runInAction(() => {
                  const {lastSyncCarouselImageId, lastSyncCatalogImageId} = this, {image: {exId}} = selectedImage,
                    carouselImageId = carouselImage?.exId ?? carouselImage?.id

                  // console.log(toJS({component: this.stores.tab?.component, lastSyncCarouselImageId, lastSyncCatalogImageId, carouselImage: _.omit(carouselImage, 'ws'), selectedImage: exId}, {recurseEverything: true}))
                  if (carouselImage && carouselImageId != lastSyncCarouselImageId) {
                    // Sidebar carousel or inline detail pane carousel changed selected image - which may not be toggled on/available in the catalog view
                    const imageInCatalog = activeCatalog.focusedCatalogItemStore.images.find(img => img.image.exId === carouselImageId)
                    if (imageInCatalog) {
                      imageInCatalog.select()
                    }

                    this.lastSyncCarouselImageId = carouselImageId
                  }
                  else {
                    // Catalog focused item changed selected image
                    const {localImages} = this,
                      index = localImages.findIndex(img => img.exId == exId)
                    if (index != -1) {
                      this.carouselStore.selectedImageIndex = index
                    }

                    this.lastSyncCarouselImageId = exId
                  }

                  // if (lastImageId != exId) {
                  //
                  //   carouselStore.selectedImageIndex = focusedImageIndex
                  // } else {
                  //   focusedCatalogItemStore.selectedImageIndex = carouselStore.selectedImageIndex
                  // }
                  //
                })
              }
            }
          }, {name: `Synchronize selected image index for individual detail tab and corresponding catalog`})
        ].filter(_.identity)

        this.loadData()
      })

    return () => {
      this.dispose()
    }
  }

  /// region Keep selected image in sync between catalog card and detail panes
  @observable lastSyncCatalogImageId: string
  @observable lastSyncCarouselImageId: string
  /// endregion

  get activeCatalog() { return this.flexLayout.activeOrLastCatalogRoot?.catalog}

  _toDispose: IDisposer[] = []
  @action dispose = () => {
    this.itemDetailsStore?.dispose()
    this.itemDetailsStore = null
    this._toDispose.forEach(d => d())
    this.modal?.dispose()

    // console.log(`Unmount CatalogItemSidebarDetailsStore`, {isForNav: this.isForNav, props: this.props})
  }

  get isApplyingMutations() { return this.itemDetailsStore?.isApplyingMutations }

  set isApplyingMutations(v) { this.itemDetailsStore.isApplyingMutations = v }

  @observable refetchOnClose = false

  get id() { return this.itemDetailsStore?.id}

  @observable.ref modal: SidebarModalStore

  @computed get sidebar(): AbstractSidebarStore & Partial<IIndividualSidebarState> {
    return this.stores.sidebar[this.align]
  }

  @computed get dragDropEvents() {
    const {catalog, data, sidebar: {selectedLibraryDefinition}} = this
    return new DragDropEventHandlers(catalog, {dragType: selectedLibraryDefinition?.index ?? catalog.index, target: selectedLibraryDefinition?.index ?? catalog.index, id: data?._id, targetExtra: {data, detailsStore: this}})
  }

  get data() { return this.itemDetailsStore?.data }

  get originalData() { return this.itemDetailsStore?.originalData }

  @computed get hideSync() {
    const {data} = this
    return !data?.exId
  }

  @computed get library() {
    const {catalog, props: {library, catalogField}} = this // this.props.library // Don't use the sidebar's library as it'll be the last item in the path, not necessarily THIS details store's data/library/id
    return library ?? (catalogField ? catalog.libraryForField(catalog._fieldByName[catalogField]) : null)
  }

  @computed get showMultipleTabs() {
    const {data} = this
    return data?.multiple !== undefined
  }

  align: SidebarAlign
  @observable.ref anchorEl: HTMLDivElement

  @observable.ref nestedObject
  @observable loading = true
  @observable isInitialLoad = true
  @observable isUploading = false
  @observable.ref uploadingImage = false
  @observable imageUploadSuccess = false
  @observable uploadError = null
  @observable isError = false
  @observable drag = false
  @observable selectedImageIndex = 0

  @computed get selectedImageId() {
    const {localImages, selectedImageIndex} = this
    return _.isEmpty(localImages) ? null : localImages[selectedImageIndex]?.exId
  }

  @observable isPublished = false

  @computed get associatedWorkspaceForCurrentImage() {
    const {selectedImage, catalog: {findFieldInTree}, workspaces: {wrapperByIndex}} = this
    if (!selectedImage?.source) return null

    const f = findFieldInTree(selectedImage.source, true)

    if (!f) return null

    return wrapperByIndex[f.esIndex]
  }

  @computed get associatedEsIndex() {
    if (!configs.deployment) return null

    const {props, library, catalog, associatedCatalogField} = this

    if (props.esIndex) return props.esIndex

    if (library) {
      const {library: {detailView}} = this
      return detailView ?? this.index
    } else if (associatedCatalogField) {
      return associatedCatalogField?.esIndex
    }

    return catalog.index
  }

  @computed get detailDef() {
    const {catalog, associatedEsIndex} = this
    return associatedEsIndex && catalog.detailViewFor(associatedEsIndex)
  }

  // region Search

  /**
   * Searching can match multiple results, user can tab through them
   */
  @observable searchTabIndex = 0

  @computed get isCreating() { return this.props.isCreate}

  @computed get isSearching() { return this.sidebar.searchText != ''}

  @computed get focusedSearchResult() {
    const {isSearching, searchResults, searchTabIndex} = this
    return isSearching ? searchResults[searchTabIndex] : null
  }

  @action search_onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.sidebar.searchText = e.target.value
    this.sidebar.overrideSearchTab = false
  }

  @action search_onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.which == Keys.ESCAPE) {
      this.searchTabIndex = 0
      this.sidebar.searchText = ''
      e.preventDefault()
      e.stopPropagation()
    } else if (e.which == Keys.TAB) {
      const {searchTabIndex, searchResults} = this
      this.searchTabIndex = e.shiftKey
                            ? searchTabIndex == 0 ? searchResults.length - 1 : searchTabIndex - 1
                            : searchTabIndex == searchResults.length - 1 ? 0 : searchTabIndex + 1

      e.stopPropagation()
    }
  }

  @computed get images(): Array<ImageWrapperDefinition> {
    const {imageSources, data} = this,
      imageSourceFields = _.uniq(_.map(imageSources ?? [], ({field}) => field)) ?? []
    return _.flatten(_.map(imageSourceFields, s => _.get(data, s)))
  }

  @action onRefreshImages = async () => {
    const {images, es: {reImportImage}} = this
    await Promise.all(images?.map(async (image) => {
      await reImportImage(image?.exId)
    }))
  }

  @computed get hasAdoptionTab(): boolean {
    const {modal} = this
    return modal?.hasAdoptionTab
  }

  @computed get hasSecondariesTab(): boolean {
    const {modal} = this
    return modal?.hasSecondariesTab
  }

  /**
   * Modal tabs which have been seen for this item (or the tab button has been moused over)
   */
  seenTabs = observable.set<string>()

  @computed get selectedDetailTab(): Partial<ModalTabConfig> {
    const {sidebar, sidebar: {overrideSearchTab}, modal: {config: {tabs}}, searchTabIndex, isSearching} = this
    let tab = sidebar.selectedTab
    if (!overrideSearchTab && isSearching && searchTabIndex < this.searchResults.length && this.searchResults[searchTabIndex].tab) {
      tab = this.searchResults[searchTabIndex].tab ?? sidebar.selectedTab
    }

    return !tabs ? {} : !tab ? _.first(tabs) : tabs.find(t => t.id == tab) ?? {}
  }

  @computed get searchResults() { return this.modal?.searchResults ?? [] }

  @computed get currentSearchResult() { return this.isSearching && this.searchResults[this.searchTabIndex]}

  // endregion

  @computed get placeholderImg() {
    const {data} = this
    const result = ((_.isArray(data?.subCategories) ? data?.subCategories : []) ?? []).map((f) => !f || !placeholderImages ? null : placeholderImages[_.toUpper(f.name)])
      .find(_.identity)
    return result
  }

  @action updateActiveImageId = (index: number) => {
    if (!this.localImages[index]) {
      index = 0
    }

    this.selectedImageIndex = index
  }

  @computed get colorwayImages() {
    return _.filter(this.localImages, img => _.lowerCase(img.source) == SPEC_TYPE.COLORWAY)
  }

  @action onDownloadImage = async (e, image) => {
    const {localImages, imageSources, colorwayImages, data, catalog} = this,
      searchSpace = [...localImages, ...colorwayImages],
      imageSource = imageSources.find(src => src.id === catalog?.workspaceConfig?._id)?.field ?? ''

    const imageObj = searchSpace.find(({id, exId}) => id === image.id || exId == image.id)

    if (imageObj) {
      if (!this.isOffline && image.downloadURL) {
        // Use nestJS download proxy to download original from C8
        window.location.href = image.downloadURL
        return
      }
      await utils.downloadImage(image.downloadURL || image.largeImageURL, this.isOffline ? image?.file?.name : imageSource == image?.source ? data?.merchProduct?.name : undefined, this.isOffline)
    } else {
      console.error(`Unable to find image object in `, searchSpace, image)
    }
  }

  @action onClickViewImage = () => {
    this.isError = false
    this.sidebar.showImageFullscreen = true
  }

  @observable presentationMode = false
  @action onTogglePresentationMode = () => {
    this.isError = false
    this.presentationMode = !this.presentationMode
  }
  @action onCloseSlider = () => {
    this.sidebar.showImageFullscreen = false
    this.isError = false
  }

  @action addImages = async (e) => {
    const files = e?.target?.files,
      {catalog: {img: {readFileImages}}} = this,
      fileImages = await readFileImages(files)
    await this.onAddImages(fileImages)
  }

  @action updateFieldData = (fieldPath: string, val: any, section?: DetailsSectionConfig, valAsString?: string) => {
    // console.log(`updateFieldData`, {fieldPath, val, section, valAsString})
    const {itemDetailsStore} = this
    // handling case when field is not present
    // if (_.has(data, fieldPath) || isCreate || field == fieldPath) {
    if (section?.id) {
      this.updateSectionData(section, fieldPath, val)
    } else {
      itemDetailsStore.updateFieldData(fieldPath, val, section, valAsString)
    }
    itemDetailsStore.onCancelEditableField()
  }

  onAddImages = async (files) => {
    const {catalog, isOffline, data, updateFieldData} = this
    let images = _.cloneDeep(data?.images) || [],
      firstImage = !images.length && files.length

    _.forEach(Array.from(files).reverse(), file => images.push(file))
    if (isOffline) {
      const uploadedImages = await catalog.img.uploadImagesToC8(images.filter(image => image?.file))
      images = [...images.filter(image => !image?.file), ...uploadedImages]
    }
    updateFieldData('images', images)
    if (!(isOffline && firstImage)) {
      data.images = images
    }
  }

  @action onChangeFileUpload = async (event) => {
    const files = event.target.files,
      {currentTarget: ct} = event,
      {catalog: {img: {readImageFromDataTransfer}}} = this

    if (files.length <= ui.settings.maxImagesPerUpload) {
      this.isError = false
      const files: any = await readImageFromDataTransfer(ct)
      this.onAddImages(files)
    } else {
      this.uploadError = `Max ${ui.settings.maxImagesPerUpload} images per upload`
      this.isError = true
    }
  }

  onDragEnterImage = (e) => {
    e.preventDefault()
    e.stopPropagation()

    const {dragDrop: {dragItem}} = this
    if (dragItem?.dragType === 'images') {
      this.drag = true
    }
  }

  onDragLeaveImage = (e) => {
    e.preventDefault()
    e.stopPropagation()

    this.drag = false
  }

  onDropImage = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    this.drag = false
    this.isError = false
    const {dataTransfer: dt} = e, {catalog: {img}} = this
    const files: any = await img.readImageFromDataTransfer(dt)
    this.onAddImages(files)
  }

  // get events() {
  //   const { onDragEnterImage, onDragLeaveImage, onDropImage } = this
  //   return { onDragEnterImage, onDragLeaveImage, onDropImage }
  // }

  @action uploadImage = async (files) => {
    const {data, catalog: {img}} = this
    const images = _.size(data.images) ? [...data.images] : []
    const {catalog: {refetch}} = this
    const imageUploadLimit = 5
    if (files.length <= imageUploadLimit) {
      this.isUploading = true
      let count = images.length > 0 ? images.length - 1 : 0

      try {
        const response = await img.resolveImages(files, count),
          imagesArray = await this.uploadImages(response, this.data._id)

        if (imagesArray.length) {
          this.data.images = imagesArray
          this.isUploading = false
          refetch('catalogItems')
          this.imageUploadSuccess = true
          setTimeout(() => {
            this.imageUploadSuccess = false
          }, 2000)
        } else {
          this.isUploading = false
        }
      } catch (e) {
        this.isUploading = false
        this.uploadError = e.message
        this.isError = true
      }
    } else {
      this.uploadError = `Max 5 images per upload`
      this.isError = true
    }
  }
//TODO: Integrate this uploadImages logic with axios APIs/GraphQL
  @action uploadImages = async (images, productId) => {
    const token = lockr.get('access_token')
    let error = ''

    let results = await Promise.all(_.map(images, async (image) => {
      if (image.file) {
        const formData = new FormData()
        formData.append('data', image.file)
        try {
          const response = await fetch(`${configs.config.graphqlEndpoint}es/gant/image-upload?docId=${productId}`, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: formData
          })

          if (response.status === 200) {
            let result = await response.json()
            return result
          } else {
            if (!error) {
              error = `Error in uploading file: ${image.file.name}`
            } else {
              error += `, ${image.file.name}`
            }
          }
        } catch (e) {
          if (!error) {
            error = `Error in uploading file: ${image.file.name}`
          } else {
            error += `, ${image.file.name}`
          }
        }
      } else {
        return Promise.resolve(image.image)
      }
    }))
    return _.filter(results[0], (result) => result && result.id)
  }

  @computed get canAddImage() {
    const {catalog: {isActionButtonEnabled}} = this
    return isActionButtonEnabled('uploadImages', this.data)
  }

  @computed get imageField() {
    return `images`
  }

  @computed get canAddImageOnData() {
    const {imageField, data, index, catalog, stores} = this,
      field: ICatalogFieldConfig = catalog._fieldByName[imageField]

    if (catalog.index !== index) {
      return false
    }

    if (_.isFunction(field?.editable)) {
      return field.editable({data, stores})
    }
    return field?.editable ?? true
  }

  get catalog() {
    const {stores: {tab, flexLayout: {activeOrLastCatalogRoot}}} = this
    if (tab?.component === 'catalogItemDetails') {
      return activeOrLastCatalogRoot?.catalog ?? super.catalog
    }
    return super.catalog
  }

  @action manageImage = async (imageId, operation, image?: ImageWrapperDefinition, callback?) => {
    this.isError = false
    if (image?.exId && operation == 'delete') {
      const {strings, stores: {flexLayout}, catalog, catalog: {dialogs: {showConfirmationProductPopup}}, updateFieldData, localImages, isOffline} = this,
        images = [...localImages].filter(v => v.source === 'images')
      showConfirmationProductPopup({
          message: strings.imageDeleteMessage,
          catalog,
          details: this,
          show: true,
          operation: 'DELETE_IMAGE',
          id: image?.__parent?._id || image?.__parent?.id,
          imageId: image?.exId,
        catalogIndex: image?.ws?.catalog?.index,
          onSuccess: () => {
            if (isOffline) {
              let index = _.findIndex(images, img => img?.exId == image?.exId)
              if (index == -1) {
                console.warn(`Unable to find image in images array`, {image, images})
              }
              else {
                images.splice(index, 1)
                updateFieldData('images', images)
                this.saveOfflineChanges()
              }
            } else {
              // Update the original builder card data if missmatched
              const {flexLayout: {previousTab}, data: {exId}} = this

              const itemDetailStores = previousTab?.catalogRoot?.catalog?.itemDetailStores
              let prevCardData = itemDetailStores ? itemDetailStores.get(exId) : null

              if (prevCardData && image?.exId) {
                const removeIndex = _.findIndex(prevCardData._data.images, img => img['exId'] == image.exId)
                // Remove image from the original builder card array
                removeIndex < 0 ? null : prevCardData._data.images.splice(removeIndex, 1)
              }
            }
            flexLayout.refetchAllCatalogRelatedTabs()
            callback && callback()
          }
        }
      )
    }
  }

  @observable isFirstRender = true
  @observable activeImageSource: string
  @action toggleActiveImageSource = (field: string) => e => {
    const {activeImageSource} = this
    this.activeImageSource = activeImageSource == field ? null : field
  }

  @computed get activeImageSources() {
    const {imageSources, activeImageSource} = this
    return !activeImageSource ? imageSources : imageSources.filter(({field}) => field == activeImageSource)
  }

  @computed get activeImageSourceByField(): Record<ImageSourceField, CatalogImageSourceConfig> {
    const {activeImageSources} = this
    return _.keyBy(activeImageSources, v => v.field)
  }

  @computed get activeImageSourceIndexByField() : Record<ImageSourceField, CatalogImageSourceConfig> {
    const {activeImageSources} = this
    return activeImageSources.reduce((acc, v, i) => ({ ...acc, [v.field]: i}), {}) as Record<ImageSourceField, CatalogImageSourceConfig>
  }

  @computed get imageSourceById() {
    return _.keyBy(this.imageSources, is => is.id)
  }

  @computed get imageSources(): Array<CatalogImageSourceConfig> {
    const {catalog, detailDef, associatedWorkspace} = this
    return detailDef?.imageSources ?? associatedWorkspace?.config?.catalog?.imageSources ?? catalog.imageSourceOptions
  }

  imagesForSource = computedFn((source: CatalogImageSourceConfig) => {
    const {data} = this, fields = [...(source.fields ?? []), source.field].filter(_.identity),
      {type} = source

    return fields.map(field => {
        let d = field ? utils.recursiveGet(data, field) : data
        if (_.isArray(d)) {
          d = d.flat()
        }

        if (d) {
          // if (multiple) {
          //   images = images.filter(_.identity).concat(..._.flatMap(d, 'images').map(i => ({...i, source: field})) ?? [])
          // } else {
          if (field === 'images' || type === 'images' || _.isArray(d)) {
            let __parent = data
            if (field && data) {
              let v = this.es.readField(data, field)?.val
              if (_.isArray(v)) v = _.first(v)
              __parent = v?.__parent
            }

            return (
              d?.map(image => image && ({...(image.position !== undefined ? image.image : _.isString(image) ? {exId: image} : image), source: field, __parent: _.isString(image) ? __parent : image?.__parent}))
            )
          } else {
            return d.images?.map(i => ({...i, source: field, __parent: d})) ?? []
          }
        }

        return null
      }).flat()
  }, {name: 'imagesForSource'})

  @computed get localImages(): Array<ImageWrapperDefinition & { __parent: any, source: string, ws?: Workspace }> {
    const {data, isCreating, activeImageSources, activeImageSourceIndexByField, imagesForSource, catalog, catalog: {findFieldInTree, imageUrlFor}, workspaces: {wrapperByIndex}, stores: {nestJSUrl}} = this
    if (isCreating) return data?.images ?? []

    let images: Array<ImageWrapperDefinition> = activeImageSources.reduce((acc, source) => [...acc, ...imagesForSource(source)], this.nestedObject ? this.nestedObject.images : []).filter(_.identity)

    const grouped = _.groupBy(
      images.map(img => {
        const f = findFieldInTree(img.source, true),
          ws = f?.esIndex ? wrapperByIndex[f?.esIndex] : catalog.workspace

        return ({
          ...img,
          imageURL: imageUrlFor(img, {c8Size: 'smallImage', autoTransparency: ws?.catalog?.features?.autoTransparency}),
          largeImageURL: imageUrlFor(img, {c8Size: 'largeImage', autoTransparency: ws?.catalog?.features?.autoTransparency}),
          downloadURL: nestJSUrl + '/image-proxy/download/' + encodeURIComponent(imageUrlFor(img, {source: 'c8', c8Size: 'image'})),
          ws
        })
      }), i => i.exId ?? i.id ?? i.file)

    // The same image can be in use in multiple places in the overall catalog item image tree
    const uniq = _.values(grouped).map(arr => arr.reduce((acc, v) => !acc || activeImageSourceIndexByField[v.source] < activeImageSourceIndexByField[acc.source] ? v : acc), null) as Array<ImageWrapperDefinition & { __parent: any, source: string, ws?: Workspace }>,
      r = _.orderBy(uniq, v => [activeImageSources.findIndex(s => v.source === s.field), images.findIndex(img => img?.exId === v?.exId)], ['asc', 'asc'])

    return r
  }

  @action mutationSaveCatalogItemDetails = async () => {
    if (this.isOffline) {
      this.saveOfflineChanges()
    }
    else {
      const {catalog} = this
      try {
        const {itemDetailsStore, secondariesStore, adoptionStore, data, data: {_id: toId}} = this,
          {settings: {attributesInCreate}} = ui

        let diff = {}
        if (!_.isEmpty(secondariesStore?.changedValues)) {
          diff = _.merge(diff, {secondaries: secondariesStore?.secondaries})
        }
        if (!_.isEmpty(adoptionStore?.changedValues)) {
          diff = _.merge(diff, {secondaries: adoptionStore.changedValues})
        }

        if (!_.isEmpty(diff)) {
          itemDetailsStore.isApplyingMutations = true
          const mutations: any = Object.keys(diff).reduce((acc, key) => {
              return _.assign(acc, {[key]: {field: key, updatedValue: (key == 'secondaries' || (attributesInCreate === false && key === 'attributes')) ? diff[key] : data[key]}})
            }, {}),
            {catalog, index} = this,
            {createItem: {updateObjectInRemote}, refetch} = catalog

          if (!_.isEmpty(mutations)) {
            const r = await updateObjectInRemote({
              toIndex: index,
              toId,
              changes: mutations
            })

            if (r === false) {
              return
            }

            itemDetailsStore.wasChanged = true
            if (itemDetailsStore.isRefetchNeeded || this.align != 'modal') {
              refetch()
            } else {
              this.refetchOnClose = true
            }
            itemDetailsStore.isRefetchNeeded = false
          }
          await itemDetailsStore.applyMutations()
          //itemDetailsStore.originalData = _.cloneDeep(itemDetailsStore.data) // this already happens in finally of applyMutations
          // this.secondariesStore?._runSecondariesQuery()
        } else {
          await itemDetailsStore.applyMutations()
        }

        //TODO - assignToField functionality pending - dragType work is pending
        //After assign from Drag show save button then assignToField is not
      } finally {
        this.isApplyingMutations = false
        this.flexLayout.refetchAllCatalogRelatedTabs()
      }
    }
  }

  @action cancelMutations = () => {
    // if (this.isOffline) {
    this.itemDetailsStore.cancelMutations()
    this.secondariesStore?.cancelChanges()

    if (!this.sidebar?.inlineModal && this.align === 'right') {
      this.sidebar.closeModal()
    }
  }

  @computed get idField() {
    if (!this.visible) return null

    return '_id'

    // const {props: {library, library: {field, nestedField, keyword}, context}} = this
    // return library ? '_id' : `${context == 'xref' && xrefId ? xrefId == '_id'
    //                                         ? xrefId : catalog.esFieldFor_byField(xrefId)
    //                                       : `${field ?? '_id'}${nestedField ? `.${nestedField}` : ''}`}${keyword ? '.keyword' : ''}`
  }

  @computed get visible() {
    const {library, sidebar, sidebar: {flexLocation, isModal}, catalog: {isMaximizedCatalogTab}} = this
    return library != null || flexLocation != null || isModal || (isMaximizedCatalogTab && sidebar.open)
  }

  @computed get idFieldVal() {
    return `${_.get(this.data, this.idField, null)}`
  }

  get where() {
    const {props: {where, idWithPath}, sidebar: {selectedPathItem}} = this
    return where ?? selectedPathItem?.where ?? [{value: idWithPath}]
  }

  @computed get offlineId() {
    const {isOffline, where, data} = this
    return isOffline ? _.first(where)?.value ?? data?._id : null
  }

  @computed get isOffline() {
    const {where} = this // Do not surface data to this destructure or you will cause an infinite loop
    if (where) {
      const value = _.first(where)?.value
      return _.isString(value) && value.includes(OFFLINE_ID)
    }

    return this.data ? this.data?._id?.includes(OFFLINE_ID) : false
  }

  @action createObjectInRemote = async () => {
    const {catalog: {dialogs}, catalog} = this
    if (this.isDirty) {
      dialogs.showConfirmationProductPopup({id: this.data.id, catalog, message: `Are you sure you want to upload this item with unsaved changes?`, operation: 'UPLOAD_PRODUCT'})
    } else {
      await this.saveCreateItem()
      setTimeout(async (detailsStore) => {
        const {updateItemPublishStatus} = detailsStore
        const timerId = setInterval(async () => {
          try {
            const {isPublished} = detailsStore
            if (isPublished) {
              clearInterval(timerId)
            }
            await updateItemPublishStatus()
          } catch (err) {
            console.error(err)
          }
        }, ui.settings.publishedItemPollInterval)

        StoresInstance.allStores.forEach(store => {
          if (store?.catalog?.index == detailsStore?.associatedEsIndex) {
            store?.catalog?.refetch()
          }
        })
      }, 0, this)
    }
  }

  private updateItemPublishStatus = async () => {
    const {getC8Item} = this.c8
    if (this.id) {
      const c8Data = await getC8Item(this.id, 'network-only')
      c8Data && (this.isPublished = true)
    }
  }

  @action saveCreateItem = async () => {
    this.isSyncing = true
    const {catalog, sidebar: {selectedPathItem}, isOffline} = this
    const newId = await catalog.createItem.createObjectInRemote(_.cloneDeep(this.data))

    if (isOffline) {
      _.last(selectedPathItem.where).value = newId
    }

    setTimeout(() => this.isSyncing = false, 10)
  }

  @computed get isValidForUpload() {
    return _.isEmpty(this.invalidFields)
  }

  /**
   * Required fields which have not yet had values filled in
   */
  @computed get invalidFields() {
    //console.log(this.data, this.isOfflineItemDirty)
    if (!this.isOffline) return true

    const valid = this.catalog.validateCatalogItem(this.data)
    return Object.keys(valid).reduce((acc, field) => _.assign(acc, {[field]: _.get(this.data, field) ? undefined : false}), {})
    // return catalog.isValidCatalogItem(this.data)
  }

  @action saveOfflineChanges = () => {
    const {catalog} = this
    try {
      this.isApplyingMutations = true
      const {data, itemDetailsStore, itemDetailsStore: {index}, secondariesStore, adoptionStore} = this
      itemDetailsStore.replaceData(data, false)
      secondariesStore && !adoptionStore && (data.secondaries = _.cloneDeep(_.size(secondariesStore.secondaries) ? secondariesStore?.secondaries ?? data?.secondaries : data?.secondaries))
      adoptionStore && (data.secondaries = _.cloneDeep(_.size(adoptionStore.adoptions) ? adoptionStore.adoptions ?? data?.secondaries : data?.secondaries))
      catalog.upsertOfflineItem({index: index || catalog.index, value: data})
    } finally {
      this.isApplyingMutations = false
      catalog.isDataTableView && catalog.refetch(catalog.index)
      // catalog.refetch() //need refetch to update Pending Item count in filter sidebar.

    }
  }

  @observable isSyncing: boolean = false

  @action _changeColor = (color) => {
    // this.data = {
    //   ...this.data,
    //   ...color
    // }
    const {itemDetailsStore: {updateFieldData}} = this
    updateFieldData('hex', color.hex)
    updateFieldData('rgb', color.rgb)
    updateFieldData('hsb', color.hsb)
  }

  @computed get searchPayload() {
    if (!this.visible) return null

    const {es, where, library, catalog, catalog: {_fieldByName}} = this,
      index = library?.index ?? catalog.index,
      _source = library?._source ?? undefined

    return removeUndefinedsNested({
      size: 1,
      query: {
        bool: {
          filter: where?.map(({field, value}) => {
            // The field is normally a catalogField which may be a different name than that in the index (for example vlp-merch-option-version.collectionVersion.name -> vlp-merch-product-version.name)
            // Lookup the foreign key if needed

            const termField = !field ? ('_id') : (_fieldByName[field]?.foreignKeys?.find(fk => fk.index === index)?.field ?? field)
            return [{'term': {[termField]: value}}]//value "C8^C100791277" id with adoption secondaries
          }) //.concat([es.filterForNonDeletedItems] as any)
        }
      },
      _source
    })
  }

  @observable hasError = false

  //@observable.ref previousData = null
  //@computed get data() { return this.previousData ?? this._data}

  @computed get secondaries() {
    const {data} = this
    return this.secondariesStore?.secondaries ?? data?.secondaries ?? []
  }

  @computed get sortedSecondaries(): Array<SecondaryData> {
    const {labelField, secondaries} = this,
      sorted = _.map(secondaries, s => _.get(s, labelField)).sort((a, b) => a.localeCompare(b, 'zh-CN'))
    return sorted?.map(s => secondaries?.find(secondary => _.get(secondary, labelField) === s)).filter(_.identity)
  }

  @computed get labelField() {
    return this.secondarySection?.secondariesTemplate?.labelField ?? 'name'
  }

  @computed get secondarySection() {
    const {stores: {sidebar: {right}}} = this
    return right?.details?.detailDef?.sidebar?.sections?.find(section => section.type == 'secondaries')
  }

  @computed get sidebarSecondarySection(): Array<DetailsViewSectionConfig> {
    const {secondarySection, labelField, sortedSecondaries} = this
    if (secondarySection) {
      return sortedSecondaries?.map(s => ({
        ...secondarySection.secondariesTemplate,
        name: `${_.get(s, labelField)}`,
        id: s.id
      }))
    }
    return []
  }

  @action _updateSecondaries = (secondaries) => {
    this.originalData.secondaries = secondaries
    this.data.secondaries = secondaries
  }

  @action clearRightSidebarDetails = () => {
    DEBUG_CATALOG_ITEM_CARDS && console.log(`Clear right sidebar details`)
    const {sidebar: {right}} = this.stores
    right.open = false
    right.path = []
    this.itemDetailsStore.replaceData(null)
  }

  @computed get offlineData() {
    const {isOffline, offlineId, catalog} = this
    if (!isOffline || !offlineId || !catalog.hasLoadedConfig) {
      return null
    } else {
      const dict = catalog.offlineItemsByIndex[this.index || catalog.index]

      return dict ? dict[this.offlineId] : null
    }
  }

  @action loadData = _.debounce(
    async () => {
      if (!this.visible || this.isOffline) {
        //console.debug(`Not visible - skipping load`)
        return null
      }

      if (!this.index || !this.stores.authStore.userIsAuthenticated) return null

      const {es, strings, stores: {isInlineDetailsStore, vipTabs}, searchPayload, catalog, catalog: {dialogs}, sidebar: {align}, where, props: {isCreate}, library, redirectToLibrary, isOffline, itemDetailsStore, index} = this

      // console.log(`loading sidebar data`, {where: toJS(where), isInitialLoad: this.isInitialLoad, hasLoadedData: this.hasLoadedData, loading: this.loading})
      if (_.isUndefined(where)) {
        // console.debug(`catalogItem - loadData() - No where clause`, {library, props: this.props})
        this.loading = false
        return null
      }
      try {
        this.hasError = false
        const id = where?.[0]?.value
        if (where?.length && id) {
          this.loading = true
          if (isOffline) {
            this.loading = false
            this.hasLoadedData = true
            this.isInitialLoad = false
            return
          }

          let data, r, meta = `SidebarDetailsStore.loadData(library: ${library?.name ?? index}, align: ${align}, where: ${stringify(where)})`

          if (where.length === 1) {
            r = await es.get({index, id, meta})
            data = r ? {...r, _id: id} : null
          } else {
            const {hits: {hits}} = await es.search(
              {
                meta,
                index,
                body: removeUndefinedsNested(searchPayload)
              }, {log: {query: false, result: false}})
            r = _.first(hits)

            //Fix bug/YFTS-4246 if no data found for any _id, redirect it to libraries left sidebar view
            if (!hits.length || r?._index != index) {
              DEBUG_SIDEBAR_DETAILS && console.debug(`No result found for specified sidebar details, redirecting to library or closing sidebar`, {searchPayload: toJS(searchPayload)})

              if (this.flexLayout.isMaximized) {
                redirectToLibrary(r?._index == index)
              }
            }

            data = r?._id ? {_id: r._id, ...r?._source} : null

            if (data && isInlineDetailsStore) {
              const vipTab = vipTabs['inlineCatalogItemDetails']
              if (vipTab && vipTab.tab.name == strings.notFound) {
                vipTab.tab.name = ''
              }
            }
          }

          // if (data) {
          //   data.secondaries = await this.fetchSecondariesData()
          // }
          // Removed when removing field from libraries
          // if (nestedField && field) {
          //   let nested = es.readField(data, field).val
          //   if (_.isArray(nested)) {
          //     nested = nested.find(v => v[nestedField] === where[0].value) // todo - need to match multiple fields
          //     _.set(data, field, nested)
          //   }
          //   this.nestedObject = nested
          // }

          runInAction(() => {
            itemDetailsStore && itemDetailsStore.replaceData(isCreate ? {} : data)
            //this.previousData = null

            // Enable/disable sync button as per the presence in C8
            this.updateItemPublishStatus()

            if (!r) {
              this.sidebar?.closeModal()
            }

            this.loading = false
            this.isInitialLoad = false
            this.hasLoadedData = true
          })
        } else {
          //dialogs.showMessage('Unable to find selected item')
          this.sidebar?.closeModal()
        }
      } catch (err) {
        runInAction(() => {
          const esError = err as ElasticSearchError

          this.hasError = true

          this.loading = false
          this.isInitialLoad = false
          this.hasLoadedData = true

          // console.log(err)
          if (esError.statusCode === 404) {
            if (isInlineDetailsStore) {
              const vipTab = vipTabs['inlineCatalogItemDetails']
              if (vipTab && vipTab.tab.name != strings.notFound) {
                vipTab.tab.name = 'Not Found'
              }
            }
          }

          this.error = err
        })
      } finally {
      }
      return isCreate ? {exId: null} : this.data
    }
    , 100)

  @observable.ref error: Error

  getTabSection = (id: string) => {
    return {
      index: _.findIndex(this.data[this.selectedDetailTab.id], (section: any) => section.id == id),
      data: _.find(this.data[this.selectedDetailTab.id], (section: any) => section.id == id)
    }
  }

  @action updateSectionData = ({id, type}: DetailsSectionConfig, field: string, value: any) => {
    switch (type) {
      case 'secondaries': {
        const {secondariesStore} = this
        secondariesStore.updateSecondariesData(id, field, value)
        console.log(`Updated ${id}.${field} with ${value}. Final object is `, toJS(this.data[this.selectedDetailTab.id], {recurseEverything: true}))
        break
      }
      case 'adoption': {
        const {adoptionStore} = this
        adoptionStore.updateAdoptionsData(id, field, value)
        console.log('update adoption value', id, field, value)
        break
      }

      default:
        throw new Error(`Unhandled update on section type '${type}'`)
    }
  }

  @observable.ref secondariesStore: DetailsSecondariesStore
  @observable.ref adoptionStore: SidebarTableRowStore

  @computed get isDirty() {
    const {itemDetailsStore, adoptionStore} = this
    return itemDetailsStore?.isDirty || (adoptionStore ? adoptionStore?.isDirty : false)
  }

  /**
   *
   * @param fieldObj
   * @param updatedValueItem
   * @param removeItem
   *
   * Dead Code
   */
  @action mutateLocalField = ({fieldObj, newValue, removeItem}: { fieldObj: DetailsViewFieldConfig, newValue: any, removeItem?: boolean }) => {
    /*const {def: type} = _.isEmpty(fieldObj?.def) ? catalog.index === this.index ? catalog._fieldByName[fieldObj.field] : catalog.fieldByWorkspaceIndex[this.index]?.fields[fieldObj.field] : fieldObj
    if (!fieldObj) {
      console.error(`Unable to find field ${fieldObj.field}`)
    }

    const {isOffline, data} = this
    if (isOffline) {
      const d = _.cloneDeep(data)
      _.set(d, fieldObj.field, newValue)
      this.data = d
      return
    }

    const {field} = fieldObj,
      baseNode = fieldObj.baseNode ?? field,
      {index: toIndex, data: {exId: toId, attributes}, idFieldVal, idField} = this,
      {catalog: {catalogConfig}} = stores,
      dragType = catalogConfig?.dragType,
      dragField = dragType?.field,
      dragName = dragType?.name,
      entityType = dragType?.entityType

    if (!field) throw new Error(`field property is not defined on ${stringify(fieldObj)}`)

    // TODO : Not sure extracting toId from data is right way, instead should start using idFieldVal, which is more configurable way
    console.log({toId, toIndex, field, idFieldVal, newValue: newValue, removeItem})

    if (_.isEmpty(this.mutationData.mutations)) {
      this.mutationData = {'toId': _.isEmpty(toId) ? idFieldVal : toId, toIndex, mutations: {}}
    }

    const updatedId = newValue?.id ?? newValue,
      updatedName = newValue?.name ?? newValue,
      mutateC8 = !fieldObj?.mutateC8 ? false : fieldObj.mutateC8

    // TODO - what is the goal here?  Specific to D&D case?
    if ((attributes && dragName && _.isArray(attributes[dragName])) || dragField === field && entityType === 'array') {
      // note the data to be rolled-back
      if (_.isEmpty(this.mutationData.mutations[field])) {
        this.rollbackMutationData[field] = _.get(this.data, field, null)
        this.mutationData.mutations[field] = {field, updatedValue: [], mutateC8, type}
      }
      const updatedArray = !_.isEmpty(attributes[dragType.name]) ? removeItem ? _.get(this.data, field, null).filter(e => e.id !== updatedId) : [...attributes[dragType.name], {id: updatedId, name: updatedName}] :
        [{id: updatedId, name: updatedName}]
      this.updateFieldData(field, updatedArray)
      this.mutationData.mutations[field].updatedValue = updatedArray.map(item => item.id)
    } else {
      runInAction(`Registering Mutation:  Set ${field} => ${stringify(newValue)}`, () => {
        // console.log(this.rollbackMutationData[field], {updatedName})
        //TODO: Logic around initializing rollbackMutationData to data which initially gets loaded, instead bulding up rollback data one by one ?

        if (_.isEqual(updatedName, this.rollbackMutationData[field]) || (!this.isDirty && _.isEqual(updatedName, _.get(this.data, field, null)))) {
          _.unset(this.mutationData.mutations, baseNode)
          _.unset(this.rollbackMutationData, field)
          this.updateFieldData(field, updatedName)
        } else {
          // note the data to be rolled-back
          if (_.isEmpty(this.mutationData.mutations[baseNode])) {
            // console.log('rollback data noted')
            this.rollbackMutationData[field] = _.get(this.data, field, null)
            // console.log(toJS(this.rollbackMutationData[field]))
          }
          // TODO: No need to pass field, we can build the fieldpath with the use of baseNode and docRef's node
          this.mutationData.mutations[baseNode] = {field, updatedValue: newValue, mutateC8, type, baseNode}
          this.updateFieldData(field, newValue)
        }

        console.log(toJS(this.mutationData))
      })
    }*/
  }

  _uploadImageAction = async (images, toId, isRefetchNeeded: boolean, originalImageId?) => {
    const files = images.updatedValue.filter(val => !val.hasOwnProperty('exId'))
    //TODO update backend to handle image upload for only ES case. WIP
    let uploadInput: IUpload = {
      entityIds: [toId],
      files: files,
      isRefetchNeeded,
      originalImageId: originalImageId
    }
    await this.catalog.img.prepareUploads(uploadInput)
  }

  /**
   * Upload Images and Reorder Images
   * API will handle the upload for ES or Prisma in Backend
   *
   * @param imagesToUpload
   */
  @action uploadReorderImages = async (imagesToUpload?: Array<ImageWrapperDefinition>) => {
    const {data: {id: toId}, data, originalData, itemDetailsStore, catalog, modal, activeImageSourceByField} = this,
      activeImageOperation = modal?.carousel?.activeImageOperation,
      {mutations: {catalogImageReorderOperation}} = catalog,
      {constants: {UPLOAD, REORDER}} = ui,
      diff: any = differenceInObject(data, originalData)

    if (diff.images) {
      delete diff.images
    }
    itemDetailsStore.isRefetchNeeded = !_.isEmpty(diff)

    switch (activeImageOperation?.imageAction) {
      case UPLOAD:
        await this._uploadImageAction(imagesToUpload, toId, !itemDetailsStore.isRefetchNeeded, activeImageOperation.originalImageId)
        break
      case REORDER:
        // await catalogImageOperation(activeImageOperation)
        const {carousel: {pendingReorderedImages, workspaceImagesBySourceAndParent}} = modal

        await Promise.all(
          _.entries(pendingReorderedImages).map(async ([field, imageDiff]) => {
            const imageSource = activeImageSourceByField[field]

            if (!imageSource) {
              console.warn(`Unable to find image source for field ${field}`, {activeImageSourceByField})
              return null
            }

            const index = catalog.indexForImageSource(imageSource)

            if (!index) {
              console.warn(`Unable to find esIndex for image source ${imageSource.id} - ${field}`)
              return null
            }

            return _.keys(imageDiff).map(async parentId => {
              const images = workspaceImagesBySourceAndParent[field][parentId]

              const input : IImageOperationInput = {
                index,
                images: images.map(v => ({exId: v.exId})),
                imageAction: 'REORDER',
                entityExId: parentId
              }

              return await catalogImageReorderOperation(input)  
            })
          }).flat())
        break
      default:
        await this._uploadImageAction(imagesToUpload, toId, !itemDetailsStore.isRefetchNeeded)
    }
    activeImageOperation && (this.modal.carousel.activeImageOperation = null)
  }

  @action reorderAllImages = async (images) => {
    const {mutations: {catalogImageReorderOperation}} = this.catalog
    await catalogImageReorderOperation(images)
  }

  @observable editableFieldText: { field: string, value, section?: DetailsSectionConfig } = null

  @computed get storeMap() {
    const {secondariesStore, adoptionStore} = this
    return {
      'secondaries': secondariesStore,
      'adoption': adoptionStore
    }
  }

  @computed get activeTabStore() {
    const {storeMap, sidebar: {tab, selectedTab}} = this
    return storeMap[tab] || storeMap[selectedTab]
  }

  @action syncDetails = (id, index) => {
    const {catalog: {dialogs}, catalog, stores: {sidebar}} = this
    this.isDirty ?
    (dialogs.showConfirmationProductPopup({id: id, message: `Are you sure you want to sync this item without saving changes?`, operation: 'SYNC_PRODUCT', catalog})) :
    this.onSyncDetails(id, index)
  }

  @computed get associatedCatalogField() {
    const {props: {catalogField}} = this
    if (catalogField) {
      return this.catalog._fieldByName[catalogField]
    }

    return null
  }

  get index() {
    if (this.props.esIndex) return this.props.esIndex

    const {library, associatedCatalogField, catalog, props} = this
    return library?.index ?? associatedCatalogField?.esIndex ?? catalog.index
  }

  @action onSyncDetails = (id, index = null) => {
    const {syncProgress} = this
    this.itemDetailsStore.cancelMutations()
    syncProgress.onStartSync({itemId: id, syncId: index || this.index})
  }

  readFieldData = (field: string, subField?: string) => {
    const {es, data} = this
    if (!data || !field) return null
    return es.readField(data, field)
  }

  dropIdFor = ({section}: { section?: DetailsSectionConfig }) => {
    const {sidebar: {selectedLibraryConfig}, selectedDetailTab, selectedDetailTab: {id: tabId}} = this

    return `${selectedLibraryConfig?.name} | ${tabId} | ${selectedDetailTab?.sections?.indexOf(section)}`
  }

  @action redirectToLibrary = _.debounce((isError) => {
    const {library, catalog: {dialogs: {showErrorMessage}}, sidebar} = this,
      errorMessage = `The specified ${_.toLower(library?.name)} could not be found`

    showErrorMessage(errorMessage)
    isError && showErrorMessage(errorMessage)
    if (!DEBUG) {
      sidebar.onGoBack()
    }
  })

  @computed get orientation(): 'horz' | 'vert' {
    const {sidebar, catalog} = this

    if (sidebar?.flexLocation === 'inlineCatalogItemDetails') {
      const {settings: {orientation}} = sidebar.tabNode.attributes.config as IFlexLayoutInlineCatalogItemDetailsConfig
      return orientation ?? 'horz'
    } else {
      return catalog.settings.details.modal.orientation ?? 'horz'
    }
  }

  @action toggleOrientation = () => {
    const {orientation, sidebar, catalog} = this,
      newOrientation = orientation !== 'horz' ? 'horz' : 'vert'

    if (sidebar?.flexLocation === 'inlineCatalogItemDetails') {
      const {settings} = sidebar.tabNode.attributes.config as IFlexLayoutInlineCatalogItemDetailsConfig
      settings.orientation = newOrientation
    } else {
      catalog.settings.details.modal.orientation = newOrientation
    }
  }
}



// WEBPACK FOOTER //
// ./src/modules/CatalogItemDetails/CatalogItemSidebarDetails/CatalogItemSidebarDetailsStore.ts